import React from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import { Text } from '~/components/core/TextComponents';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type { AdditionalInformationSection } from '~/components/types/incident-configuration-types';

import copies from '../../copies.json';
import type { CategoryLimitOption } from '../../PolicyCategoriesOptions';

import { nextSectionFallbackKey, RESERVED_SECTION_IDS, SECTION_KEY_MAX_LENGTH, SECTION_KEY_PATTERN } from './constants';

const TEXTS = copies.additional_information;

type AddCustomSectionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (section: AdditionalInformationSection) => void;
  existingKeys: string[];
  categories: CategoryLimitOption[];
};

type DialogValues = {
  desc: string;
  key: string;
  incident_sub_type_categories: string[];
  active: boolean;
  mandatory: boolean;
};

const AddCustomSectionDialogInner = ({
  onCancel,
  categories,
  existingKeys,
}: {
  onCancel: () => void;
  categories: CategoryLimitOption[];
  existingKeys: string[];
}) => {
  const { values, isSubmitting } = useFormikContext<DialogValues>();
  // Derive the Key from the Display Name (snake_case) while authoring; never disabled here — the Key is
  // only frozen after the section is saved (on the section card, where it renders disabled). snakeCase can
  // leave characters outside the ASCII key charset (lodash lowercases Cyrillic rather than dropping it,
  // and keeps other non-Latin letters), so strip those and tidy the underscores they leave behind —
  // "Roof דליפה" → "roof". Fall back to a unique `section_<n>` when the derived key would be unusable:
  // nothing valid survives (a fully non-Latin / punctuation-only name leaves an empty body), or it lands
  // on an existing key — otherwise a name like "Roof" would auto-fill a key that then fails Save on a
  // field the user never touched (ids stay ASCII, no transliteration).
  // The scans are over small lists, so this needn't be memoized.
  const derivedBody = snakeCase(values.desc)
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  // Cap the id at the length limit so a very long Display Name can't silently derive an over-limit
  // (invalid) key; drop a trailing underscore the slice may leave.
  const derivedKey = derivedBody.slice(0, SECTION_KEY_MAX_LENGTH).replace(/_+$/, '');
  // Also reject a derived key that a built-in section owns: "Documents" derives `documents`, which is
  // reserved, and auto-filling it would fail Save on a field the user never touched -- the exact case the
  // fallback exists for.
  const isDerivedKeyUsable =
    SECTION_KEY_PATTERN.test(derivedKey) && !existingKeys.includes(derivedKey) && !RESERVED_SECTION_IDS.has(derivedKey);
  useSetDefaultFieldsOnChange(
    values.desc,
    { key: isDerivedKeyUsable ? derivedKey : nextSectionFallbackKey(existingKeys) },
    null,
    false,
    // doNotIgnoreEmptyPrev + ignoreOnMount: derive even on the first change from the empty initial value,
    // so a paste or a one-character Display Name fills the Key too (the hook otherwise fires only once the
    // previous value is truthy, i.e. from the second keystroke on), while not clobbering the empty Key on open.
    true,
    true
  );

  return (
    <Form className="flex flex-col gap-12">
      <TextFieldFormik id="desc" name="desc" label={TEXTS.display_name_label} fullWidth />
      {/* helperText renders under the field and is replaced by the validation error when one fires, so the
          charset hint and the error never stack (TextFieldFormik: `helperText={fieldError || helperText}`). */}
      {/* Same rule/wording as the validation error — reuse the one copy so it can't drift. */}
      <TextFieldFormik id="key" name="key" label={TEXTS.key_label} helperText={TEXTS.errors.key_pattern} fullWidth />
      <ArrayMultiselectFieldWithChipsFormik
        id="incident_sub_type_categories"
        label={TEXTS.limit_to_categories_label}
        options={categories}
        disabled={categories.length === 0}
      />
      <div className="flex items-center gap-24">
        <SwitchFormik id="active" label={TEXTS.show_label} hideErrorGap />
        <CheckboxFormik id="mandatory" label={TEXTS.required_label} />
      </div>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
        {TEXTS.empty_section_note}
      </Text>
      <div className="flex justify-end">
        <Button variant="outlined" color="primary" className="mr-16" onClick={onCancel} disabled={isSubmitting}>
          {TEXTS.cancel}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {TEXTS.save}
        </Button>
      </div>
    </Form>
  );
};

const AddCustomSectionDialog = ({
  isOpen,
  onClose,
  onSubmit,
  existingKeys,
  categories,
}: AddCustomSectionDialogProps) => {
  return (
    <CardDialog isDialog fullWidth open={isOpen} onClose={onClose} title={TEXTS.dialog_title}>
      <Formik
        initialValues={{
          desc: '',
          key: '',
          incident_sub_type_categories: [],
          active: true,
          mandatory: false,
        }}
        validationSchema={Yup.object().shape({
          desc: Yup.string().trim().max(255, TEXTS.errors.display_name_max).required(TEXTS.errors.required),
          key: Yup.string()
            .trim()
            .required(TEXTS.errors.required)
            .matches(SECTION_KEY_PATTERN, TEXTS.errors.key_pattern)
            .max(SECTION_KEY_MAX_LENGTH, TEXTS.errors.key_max)
            .notOneOf(existingKeys, TEXTS.errors.key_unique)
            // `existingKeys` covers the org's OTHER custom sections; built-in ids are never in that list,
            // so they need this separate check. Same rule and wording as the save-time schema and the
            // backend, so the user sees one sentence wherever they hit it.
            .test('key-not-reserved', TEXTS.errors.key_reserved, (value) => !RESERVED_SECTION_IDS.has(value ?? '')),
        })}
        onSubmit={(values: DialogValues) => {
          // yup's `.trim()` is a cast Formik discards, so a manually typed "  roof_damage  " reaches here
          // verbatim; trim on write so the stored id/desc match what the field-level schema validated (an
          // untrimmed id fails SECTION_KEY_PATTERN in fnolConfigurationValidationSchema and blocks save).
          onSubmit({
            id: values.key.trim(),
            desc: values.desc.trim(),
            incident_sub_type_categories: values.incident_sub_type_categories,
            active: values.active,
            mandatory: values.mandatory,
          });
        }}
      >
        <AddCustomSectionDialogInner onCancel={onClose} categories={categories} existingKeys={existingKeys} />
      </Formik>
    </CardDialog>
  );
};

export default AddCustomSectionDialog;
